import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { ValidationMessageList } from './ValidationMessageList';
import { usePatchStagingRow } from '../useStagingRows';
import type { StagingRowDto, ValidationMessage } from '../types';

interface StagingRowDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: StagingRowDto | null;
  jobId: string | undefined;
  /** Focus the editable control as soon as the drawer opens for this row
   *  (spec-13 §8.5 — opened FROM a validation message, round-6 item 8). */
  focusOnOpen?: boolean;
}

export const StagingRowDrawer = ({
  open,
  onOpenChange,
  row,
  jobId,
  focusOnOpen = false,
}: StagingRowDrawerProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const patch = usePatchStagingRow(jobId);
  const [editValue, setEditValue] = useState('');
  const [reason, setReason] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  // spec-13 §8.5: selecting a validation message focuses the drawer's
  // editable payload control. `normalized_payload` is a single JSON textarea
  // here — there is no per-cell control to target — so every selection
  // focuses that one editable control. The fallback branch only fires if a
  // future drawer layout omits an editable control entirely.
  const editableRef = useRef<HTMLTextAreaElement | null>(null);
  const [selectFocusFallback, setSelectFocusFallback] = useState(false);
  const canViewTechnicalDetails = hasPermission(currentUser, 'system.admin');

  useEffect(() => {
    if (row) {
      setEditValue(JSON.stringify(row.normalized_payload ?? {}, null, 2));
      setReason('');
      setParseError(null);
      setReasonError(null);
      setSelectFocusFallback(false);
    }
  }, [row]);

  useEffect(() => {
    if (!open || !row || !focusOnOpen) return;
    // Let the sheet mount its content before moving focus.
    const id = window.setTimeout(() => {
      if (editableRef.current) {
        setSelectFocusFallback(false);
        editableRef.current.focus();
      } else {
        setSelectFocusFallback(true);
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, row, focusOnOpen]);

  const handleSelectMessage = (_msg: ValidationMessage) => {
    if (editableRef.current) {
      setSelectFocusFallback(false);
      editableRef.current.focus();
    } else {
      setSelectFocusFallback(true);
    }
  };

  const handleSave = async () => {
    if (!row) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(editValue) as Record<string, unknown>;
    } catch {
      setParseError(t('efs.imports.review.drawer.edit_hint', { ns: 'imports' }));
      return;
    }
    const trimmed = reason.trim();
    if (trimmed.length < 1) {
      setReasonError(
        t('efs.imports.review.drawer.reason_required', {
          ns: 'imports',
          defaultValue: 'Correction reason is required',
        }),
      );
      return;
    }
    await patch.mutateAsync({
      rowId: row.staging_row_id,
      normalizedPayload: parsed,
      correctionReason: trimmed,
      status: 'corrected',
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('efs.imports.review.drawer.title', { ns: 'imports' })}</SheetTitle>
          <SheetDescription>
            {row ? (
              <span className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
                  {t(`efs.imports.row_types.${row.row_type}`, {
                    ns: 'imports',
                    defaultValue: row.row_type,
                  })}
                </span>
                <span className="font-mono text-xs text-efs-text-muted">
                  {row.source_sheet_name ?? '—'} / {row.source_row_number ?? '—'}
                </span>
              </span>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        {row ? (
          <div className="mt-6 space-y-6">
            <section>
              <h3 className="mb-2 text-sm font-semibold text-efs-text-primary">
                {t('efs.imports.review.drawer.validation_messages', { ns: 'imports' })}
              </h3>
              <ValidationMessageList
                messages={row.validation_messages}
                sheet={row.source_sheet_name}
                rowNumber={row.source_row_number}
                onSelect={handleSelectMessage}
                showTechnicalDetails={canViewTechnicalDetails}
              />
              {selectFocusFallback ? (
                <p className="mt-2 text-xs text-efs-text-muted">
                  {t('efs.issues.import.focus_fallback', { ns: 'issues' })}
                </p>
              ) : null}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-efs-text-primary">
                {t('efs.imports.review.drawer.raw_payload', { ns: 'imports' })}
              </h3>
              <pre className="max-h-64 overflow-auto rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-xs">
                {JSON.stringify(row.raw_payload, null, 2)}
              </pre>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-efs-text-primary">
                {t('efs.imports.review.drawer.normalized_payload', { ns: 'imports' })}
              </h3>
              <Textarea
                ref={editableRef}
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value);
                  setParseError(null);
                }}
                rows={12}
                className="font-mono text-xs"
                aria-label={t('efs.imports.review.drawer.normalized_payload', { ns: 'imports' })}
              />
              {parseError ? (
                <p className="mt-1 text-xs text-efs-error">{parseError}</p>
              ) : (
                <p className="mt-1 text-xs text-efs-text-muted">
                  {t('efs.imports.review.drawer.edit_hint', { ns: 'imports' })}
                </p>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-efs-text-primary">
                {t('efs.imports.review.drawer.correction_reason', {
                  ns: 'imports',
                  defaultValue: 'Correction reason',
                })}
              </h3>
              <Textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError(null);
                }}
                rows={3}
                className="text-sm"
                aria-label={t('efs.imports.review.drawer.correction_reason', {
                  ns: 'imports',
                  defaultValue: 'Correction reason',
                })}
              />
              {reasonError ? <p className="mt-1 text-xs text-efs-error">{reasonError}</p> : null}
            </section>
          </div>
        ) : null}

        <SheetFooter className="pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={patch.isPending}
          >
            {t('efs.common.actions.cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={patch.isPending || !row}>
            {patch.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('efs.imports.review.actions.save', { ns: 'imports' })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
